import React, { useRef, useState, useLayoutEffect, createContext, useContext, useCallback, useMemo, useEffect } from 'react'
import {
  Button,
  Combobox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExpandIcon,
  InputWithCounter,
  LoadingButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  ZyguanbiIcon,
} from '@/component'
import { type UseFormReturn, type FieldValues } from 'react-hook-form'
import { ClearButton } from './filter'
import {
  postUserQueryTemplateAdd,
  postUserQueryTemplateDelete,
  getUserQueryTemplateList,
  type UserQueryTemplate,
} from '@/service/fabric-api'
import { X } from 'lucide-react'
import { ConfirmDialog } from '../dialog/confirm-dialog'
import { ComboboxEmpty } from '@/component/ui/combobox/common'

// 定义筛选项类型
type FilterItem = {
  name: string
  value: string
  label: React.ReactNode
  deletable?: boolean // 控制筛选项是否可删除，默认为true
}

// 创建筛选器上下文
type FilterSummaryContextType = {
  filters: FilterItem[]
  setFilters: (newFilters: FilterItem[]) => void
  addFilter: (filter: FilterItem) => void
  removeFilter: (name: string) => void
  clearFilters: () => void
  formInstance?: UseFormReturn<FieldValues>
  registerForm: (form: UseFormReturn<FieldValues>) => void
  presetSource?: string
  presets: UserQueryTemplate[]
  getPresets: () => void
}

const FilterSummaryContext = createContext<FilterSummaryContextType>({
  filters: [],
  setFilters: () => {},
  addFilter: () => {},
  removeFilter: () => {},
  clearFilters: () => {},
  registerForm: () => {},
  presetSource: undefined, // 新增presetSourc
  presets: [], // 新增presets
  getPresets: () => [],
})

// 提供筛选器上下文的Provider组件
export const FilterSummaryProvider: React.FC<{ children: React.ReactNode; presetSource?: string }> = ({
  children,
  presetSource,
}) => {
  const [filters, setFilters] = useState<FilterItem[]>([])
  const [formInstance, setFormInstance] = useState<UseFormReturn<FieldValues> | undefined>(undefined)
  const [presets, setPresets] = useState<UserQueryTemplate[]>([])

  // 注册表单实例
  const registerForm = useCallback((form: UseFormReturn<FieldValues>) => {
    setFormInstance(form)
  }, [])

  // 使用useCallback确保函数引用稳定
  const addFilter = useCallback((filter: FilterItem) => {
    setFilters((prevFilters) => {
      // 优化: 首先检查是否真的需要更新
      const existingIndex = prevFilters.findIndex((item) => item.name === filter.name)
      if (existingIndex !== -1) {
        const existingFilter = prevFilters[existingIndex]
        // 如果值和标签都相同，不做任何更改
        if (
          existingFilter.value === filter.value &&
          JSON.stringify(existingFilter.label) === JSON.stringify(filter.label) &&
          existingFilter.deletable === filter.deletable
        ) {
          return prevFilters
        }

        // 否则替换现有筛选项
        const newFilters = [...prevFilters]
        newFilters[existingIndex] = filter
        return newFilters
      }

      // 添加新的筛选项
      return [...prevFilters, filter]
    })
  }, [])

  // 移除筛选项并清除对应表单值
  const removeFilter = useCallback(
    (name: string) => {
      // 1. 从筛选摘要中移除
      setFilters((prevFilters) => {
        // 优化: 检查是否存在需要移除的筛选项
        const existingIndex = prevFilters.findIndex((item) => item.name === name)
        if (existingIndex === -1) {
          return prevFilters
        }
        return prevFilters.filter((item) => item.name !== name)
      })

      // 2. 如果存在表单实例，清除对应字段的值
      if (formInstance) {
        try {
          // 使用setValue设置字段值为空
          const fieldType = Array.isArray(formInstance.getValues(name)) ? [] : ''
          if (
            !formInstance.getValues(name) ||
            (Array.isArray(formInstance.getValues(name)) && formInstance.getValues(name).length === 0)
          )
            return
          formInstance.setValue(name, fieldType)
        } catch (error) {
          console.error(`清除字段 ${name} 值时出错:`, error)
        }
      }
    },
    [formInstance],
  )

  // 清除所有筛选项并重置表单
  const clearFilters = useCallback(() => {
    setFilters((pre) => pre.filter((item) => !item.deletable))
    // 如果存在表单实例，重置表单
    if (formInstance) {
      formInstance.reset()
    }
  }, [formInstance])

  const getPresets = useCallback(() => {
    getUserQueryTemplateList({
      source: presetSource,
    }).then((res) => {
      setPresets(res)
    })
  }, [presetSource])
  // 使用useMemo确保上下文值稳定
  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      addFilter,
      removeFilter,
      clearFilters,
      formInstance,
      registerForm,
      presetSource,
      presets,
      getPresets,
    }),
    [filters, addFilter, removeFilter, clearFilters, formInstance, registerForm, presetSource, presets, getPresets],
  )

  return <FilterSummaryContext.Provider value={contextValue}>{children}</FilterSummaryContext.Provider>
}

// 自定义Hook，用于访问筛选器上下文
export const useFilterSummary = () => useContext(FilterSummaryContext)

/**
 * 简化版的SelectedFilters组件，更紧凑的布局，适合内嵌在筛选面板中
 */
const SelectedFiltersSummary = ({
  filters: propFilters,
  onDelete,
  useContext: useContextValue = false,
}: {
  filters?: FilterItem[]
  onDelete?: (name: string) => void
  useContext?: boolean // 是否使用上下文中的筛选器
}) => {
  const filterContext = useFilterSummary()
  const filters = useContextValue ? filterContext.filters : propFilters || []
  const handleDelete = useContextValue ? filterContext.removeFilter : onDelete

  const containerRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [visibleFilters, setVisibleFilters] = useState<FilterItem[]>([])
  const [hiddenFilters, setHiddenFilters] = useState<FilterItem[]>([])
  const [showTooltip, setShowTooltip] = useState(false)
  // 使用useCallback稳定化checkOverflow函数
  const checkOverflow = useCallback(() => {
    const container = containerRef.current
    if (!container || filters.length === 0) return

    // 重置状态
    setVisibleFilters(filters)
    setHiddenFilters([])

    // 在下一个渲染周期检查溢出
    setTimeout(() => {
      if (!container) return
      const isOverflow = container.scrollWidth > container.clientWidth
      setIsOverflowing(isOverflow)

      if (isOverflow) {
        // 如果溢出，计算可见筛选项
        const items = container.querySelectorAll('.filter-item')
        const containerWidth = container.clientWidth
        let totalWidth = 180 // 预留给"已选择"和"更多"按钮的空间
        let visibleCount = 0

        items.forEach((item) => {
          const itemWidth = (item as HTMLElement).offsetWidth
          if (totalWidth + itemWidth < containerWidth) {
            totalWidth += itemWidth
            visibleCount++
          }
        })

        setVisibleFilters(filters.slice(0, visibleCount))
        setHiddenFilters(filters.slice(visibleCount))
      }
    }, 0)
  }, [filters])

  // 检测溢出
  useLayoutEffect(() => {
    if (!containerRef.current || filters.length === 0) {
      setIsOverflowing(false)
      setVisibleFilters(filters)
      setHiddenFilters([])
      return
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [filters, checkOverflow])

  if (filters.length === 0) {
    return null
  }

  return (
    <div ref={containerRef} className="flex items-center overflow-hidden">
      <span className="line-clamp-1 min-w-max ml-2 text-muted-foreground-2">已选择({filters.length})</span>
      <div className="flex items-center ml-2">
        {visibleFilters.map((filter) => (
          <div
            key={filter.name}
            className="filter-item rounded inline-flex h-8 max-w-55 shrink-0 items-center gap-1 bg-page pl-3 pr-2 hover:bg-shadow visible mr-2"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="line-clamp-1 break-all">
                    {filter.label}:{filter.value}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-120 max-h-75 overflow-auto">
                  <div>
                    {filter.label}:{filter.value}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {handleDelete && filter.deletable !== false && (
              <button type="button" onClick={() => handleDelete(filter.name)}>
                <ZyguanbiIcon size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {isOverflowing && hiddenFilters.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="h-8 w-8 ml-1 inline-flex items-center justify-center rounded p-1 bg-page hover:bg-shadow"
              onClick={() => setShowTooltip(!showTooltip)}
            >
              <ExpandIcon size={16} className="rotate-90" />
            </button>
          </TooltipTrigger>
          <TooltipContent className=" bg-background text-foreground p-4 max-w-75 max-h-75 min-w-75 overflow-auto">
            <div className="flex flex-col gap-4 text-sm">
              {hiddenFilters.map((filter) => (
                <div key={filter.name} className="flex items-center justify-between ">
                  <span>
                    {filter.label}：{filter.value}
                  </span>
                  {handleDelete && filter.deletable !== false && (
                    <button type="button" onClick={() => handleDelete(filter.name)}>
                      <ZyguanbiIcon size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

const ResetFiltersButton = () => {
  const { clearFilters, filters } = useFilterSummary()
  const handleReset = useCallback(() => {
    clearFilters()
  }, [clearFilters])
  return filters.filter((f) => f.deletable)?.length > 0 ? <ClearButton onClear={handleReset}></ClearButton> : null
}

const SetPresetCombobox = () => {
  const { presetSource, presets, getPresets, formInstance } = useFilterSummary()
  const [open, setOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<number>(0)

  useEffect(() => {
    if (presetSource) {
      getPresets()
    }
  }, [])

  function handleSetPreset(value?: string) {
    if (!value) return
    const preset = presets.find((m) => m.id === Number(value))
    if (!preset || !preset.content) return
    const presetFilters = JSON.parse(preset.content) as FilterItem[]
    const filterProps = new Map<string, unknown>([
      ['pageNo', 1],
      ['limit', null],
      ['pageSize', null],
      ['sortField', null],
      ['sortType', null],
    ])
    formInstance?.reset()
    for (const key in presetFilters) {
      if (filterProps.has(key)) {
        if (filterProps.get(key)) {
          formInstance?.setValue(key, filterProps.get(key))
        }
      } else {
        formInstance?.setValue(key, presetFilters[key])
      }
    }
  }

  function handleDeleteConfirm() {
    postUserQueryTemplateDelete({
      source: presetSource,
      id: deleteId,
    })
      .then(() => {
        getPresets()
      })
      .finally(() => {
        setOpen(false)
      })
  }

  return (
    presetSource && (
      <>
        <Combobox
          className="px-0"
          showSearch={false}
          placeholder={`快速筛选项(${presets.length}/10)`}
          onValueChange={(val) => handleSetPreset(val)}
        >
          <ComboboxEmpty />
          {presets.map((m) => (
            <Combobox.Option key={m.id} value={`${m.id}`}>
              <div className="flex justify-between w-full">
                {m.title}
                <div
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDeleteId(m.id!)
                    setOpen(true)
                  }}
                >
                  <X size={16} />
                </div>
              </div>
            </Combobox.Option>
          ))}
        </Combobox>
        <ConfirmDialog
          type="delete"
          open={open}
          onOpenChange={setOpen}
          onCancel={() => setOpen(false)}
          onConfirm={async () => handleDeleteConfirm()}
          title="删除确认"
          description="是否确认删除筛选模板？"
        />
      </>
    )
  )
}

const SavePresetButton = () => {
  const { filters, formInstance, presetSource, presets, getPresets } = useFilterSummary()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(false)
  async function handleSavePreset() {
    try {
      setLoading(true)
      await postUserQueryTemplateAdd({
        source: presetSource!,
        title,
        content: JSON.stringify(formInstance?.getValues()),
      })
      setLoading(false)
      setOpen(false)
      setTitle('')
      getPresets()
    } catch (error) {
      setLoading(false)
      console.error('保存模板失败:', error)
    }
  }
  return (
    <div className="flex items-center">
      {presetSource && filters.length > 0 && (
        <Button
          type="button"
          variant="ghost"
          disabled={presets.length >= 10}
          className="cursor-pointer"
          onClick={() => {
            setOpen(true)
          }}
        >
          保存选项
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <span>添加快速筛选</span>
            </DialogTitle>
          </DialogHeader>
          <InputWithCounter
            wrapperClassName="w-full"
            placeholder="请输入模板名称（15字以内）"
            value={title}
            onValueChange={(val) => setTitle(val)}
            maxLength={15}
          ></InputWithCounter>
          <DialogFooter>
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <LoadingButton loading={loading} onClick={handleSavePreset}>
                确定
              </LoadingButton>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
export { SelectedFiltersSummary, ResetFiltersButton, SetPresetCombobox, SavePresetButton }
export type { FilterItem }
